import _ from 'lodash';
import invariant from 'invariant';

import AuthActions from 'auth/AuthActions';
import {
  RESET_PASSWORD_TOKEN_COOKIE,
  RESET_PASSWORD_CODE_COOKIE,
  RESET_PASSWORD_SMS_ADDRESS_COOKIE,
} from 'constants/AuthConstants';
import cookies from 'lib/Cookies';
import CurrentUser from 'auth/CurrentUser';
import DeviceStore from 'stores/DeviceStore';
import DeviceActions from 'actions/DeviceActions';
import GroupActions from 'groups/actions/GroupActions';
import ToastActions from 'actions/ToastActions';
import User from 'entities/User';
import { dispatcher } from 'lib/Dispatcher';
import errorLogger from 'utils/errorLogger';
import injectUserIntoApollo from 'auth/injectUserIntoApollo';
import RemindAPI from 'lib/RemindAPI';
import { ReportOptions, ReportOtherOption } from 'constants/ReportConstants';
import REPORT_USER_MUTATION from 'user/mutations/reportUser.gql';

import { ActionTypes } from './UserConstants';

// FIXME looks like we never check current_password?!
const editPassword = (values: { current_password?: string | null; password: string }) => {
  const { password } = values;

  const signInWithDevice = () => {
    const devices = DeviceStore.getDevices();
    const emailOrPhoneDevice = devices.find(
      (d) => d.getType() === 'sms' || d.getType() === 'email'
    );
    return AuthActions.signIn(emailOrPhoneDevice.getEndpoint(), password).then((user) => {
      dispatcher.dispatch({
        type: ActionTypes.USER_UPDATED,
        user,
      });
    });
  };

  const updatePassword = () => {
    return User.update(values).then(signInWithDevice);
  };

  return DeviceActions.loadDevices().then(updatePassword);
};

const UserActions = {
  // TODO does this require a token or not?
  loadCurrentUser(token?: string | null) {
    return User.fetch({ token }).then((user) => {
      dispatcher.dispatch({
        type: ActionTypes.LOAD_USER,
        user,
      });

      return user;
    });
  },

  update(attrs: any): Promise<User> {
    return User.update(attrs).then((user) => {
      if (
        CurrentUser.isSignedIn() &&
        !_.isEqual(CurrentUser.getUser().getOrganizationIds(), user.getOrganizationIds())
      ) {
        GroupActions.loadGroups();
      }

      dispatcher.dispatch({
        type: ActionTypes.USER_UPDATED,
        user,
      });

      try {
        injectUserIntoApollo(user, false);
      } catch (e) {
        console.error('user update - failed to insert user into cache: ', e);
      }

      return user;
    });
  },

  updateLocal(attrs: any): void {
    const user = CurrentUser.getUser();

    user.set(attrs);

    dispatcher.dispatch({
      type: ActionTypes.USER_UPDATED,
      user,
    });
  },

  createPassword(password: string) {
    return editPassword({ password });
  },

  changePassword(currentPassword: string | undefined | null, newPassword: string) {
    const values = { password: newPassword, current_password: currentPassword };
    return editPassword(values);
  },

  create(attrs: any) {
    return User.create(attrs).then(AuthActions.onSessionCreated);
  },

  delete(token?: string | null, reason?: string | null) {
    return User.delete(token, reason).then(() => {
      dispatcher.dispatch({
        type: ActionTypes.USER_DELETED,
      });
    });
  },

  verifyDeleteToken(token: string) {
    return User.verifyDeleteToken(token);
  },

  confirm(token: string, source: string) {
    return User.confirm(token, source);
  },

  requestConfirmation() {
    return User.requestConfirmation();
  },

  savePreference(name: string, value: unknown) {
    const currentUser = CurrentUser.getUser();

    return currentUser.savePreference(name, value).then((preferences) => {
      dispatcher.dispatch({
        type: ActionTypes.PREFERENCES_UPDATED,
        preferences,
      });

      return preferences;
    });
  },

  setRole(role: string): void {
    User.setRole(role)
      .then((user) => {
        dispatcher.dispatch({
          type: ActionTypes.USER_UPDATED,
          user,
        });
      })
      .catch(errorLogger('User.setRole failure'));
  },

  resetPassword(password: string): Promise<User> {
    invariant(cookies.isEnabled(), 'resetPassword called with cookies disabled');
    const token = cookies.get(RESET_PASSWORD_TOKEN_COOKIE);
    const code = cookies.get(RESET_PASSWORD_CODE_COOKIE);
    const smsAddress = cookies.get(RESET_PASSWORD_SMS_ADDRESS_COOKIE);
    cookies.expire(RESET_PASSWORD_TOKEN_COOKIE);
    cookies.expire(RESET_PASSWORD_CODE_COOKIE);
    cookies.expire(RESET_PASSWORD_SMS_ADDRESS_COOKIE);
    return token
      ? User.resetPasswordWithToken(password, token)
      : // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        User.resetPasswordWithCode(password, code, smsAddress);
  },

  setBirthdate(birthdate: Date): Promise<void> {
    return User.setBirthdate(birthdate).then((user) => {
      dispatcher.dispatch({
        type: ActionTypes.USER_UPDATED,
        user,
      });
    });
  },

  setParentEmail(parentEmail: string): Promise<void> {
    // TODO: Add error handling here
    return User.setParentEmail(parentEmail).then((user) => {
      dispatcher.dispatch({
        type: ActionTypes.USER_UPDATED,
        user,
      });
    });
  },

  requestForPublicGroups(teacherId: number): Promise<void> {
    return User.requestForPublicGroups(teacherId).then(() => {
      ToastActions.showSuccessToast('Invite sent');
    });
  },

  reportUser(
    userUuid: string,
    key: string,
    notes: string,
    distanceLearningSessionUUID?: string
  ): Promise<void> {
    const reason = ReportOptions[key] ?? ReportOtherOption;
    const reasonAndNotes = notes ? `${reason} - ${notes}` : reason;

    return RemindAPI.execute({
      mutation: REPORT_USER_MUTATION,
      variables: {
        input: {
          userUuid,
          reason: reasonAndNotes,
          key,
          distanceLearningSessionUUID,
        },
      },
    }).then(
      (res) => {
        // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
        const { error } = res.reportUser;
        if (error) {
          console.error(`reportUser failed - ${error.message}`);
          ToastActions.showErrorToast('Could not submit report');
        } else {
          ToastActions.showSuccessToast('Report submitted');
        }
      },
      (error) => {
        console.error(`reportUser failed - ${error.message}`);
        ToastActions.showErrorToast('Could not submit report');
      }
    );
  },

  addParent(name: string, email: string) {
    return User.addParent(name, email).then((user) => {
      dispatcher.dispatch({
        type: ActionTypes.USER_UPDATED,
        user,
      });
      return user;
    });
  },
};

export default UserActions;
